import React, { useEffect, useState } from 'react';
import { Button, Alert } from 'components';
import { Props } from 'types/modals';
import Base from '../../base';
import { Chainlink, Timer, Send2, TickCircle, TickSquare, Warning2 } from 'iconsax-react';
import useApi from 'hooks/useApi';
import endpoints from 'constants/endpoints';

interface EmployeeData {
  id: number;
  check_employee_id: string;
  legal_first_name: string;
  legal_last_name: string;
  fixed: boolean;
  noValidBankAccount?: boolean;
  noCheckOutTime?: boolean;
  checkInAfterCheckout?: boolean;
  jobDurationTooLong?: boolean;
}

const FixPayrollModal = ({ closing, onClose, params: { id } }: Props) => {
  const listEmployeesApi = useApi();
  const fixEmployeeApi = useApi();

  const [employees, setEmployees] = useState<EmployeeData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [started, setStarted] = useState<boolean>(false);
  const [finished, setFinished] = useState<boolean>(false);

  const start = async () => {
    setStarted(true);

    let employeesCopy = [...employees];

    for (const employee of employeesCopy) {
      console.log(employee.check_employee_id, id);
      const response = await fixEmployeeApi.fetchData(endpoints.FIX_PROBLEMS_PAYROLL.get(), {
        checkEmployeeId: employee.check_employee_id,
        payrollId: Number(id)
      });

      const { noValidBankAccount, noCheckOutTime, checkInAfterCheckout, jobDurationTooLong } =
        response.unfixableProblems;

      employeesCopy = employeesCopy.map(e => ({
        ...e,
        ...(e.check_employee_id === employee.check_employee_id
          ? { fixed: true, noValidBankAccount, noCheckOutTime, checkInAfterCheckout, jobDurationTooLong }
          : {})
      }));

      setEmployees(employeesCopy);
    }

    setFinished(true);
  };

  useEffect(() => {
    if (listEmployeesApi.response) {
      setIsLoading(false);
      setEmployees((listEmployeesApi.response.employees ?? []).map((e: object) => ({ ...e, issues: null })));
    }
  }, [listEmployeesApi.response]);

  useEffect(() => {
    listEmployeesApi.fetchData(endpoints.FIX_PROBLEMS_PAYROLL_EMPLOYEE_LIST.get({ id }));
  }, []);

  const hasIssues = (issues: EmployeeData) => {
    return (
      issues.noValidBankAccount || issues.noCheckOutTime || issues.checkInAfterCheckout || issues.jobDurationTooLong
    );
  };

  return (
    <Base header="Fix Payroll" closing={closing} onClose={onClose} size="lg" className="fix-payroll-modal">
      {isLoading ? (
        <div className="loading-container">
          <Chainlink className="loading" color="black" />
        </div>
      ) : (
        <div>
          {!started ? (
            <div className="start-button-container">
              <Button text="Start" icon={<Send2 color="white" />} onClick={start} />
            </div>
          ) : !finished ? (
            <div className="fix-in-progress-container">
              <Chainlink className="loading" color="black" />
            </div>
          ) : (
            <div className="finished-container">
              <Alert message="Payroll problems fixed." type="success" icon={<TickSquare size={32} color="green" />} />
            </div>
          )}
          <table>
            <thead>
              <tr>
                <th>Check Employee ID</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Status</th>
                <th>Issues</th>
              </tr>
            </thead>
            <tbody>
              {employees.map(employee => (
                <tr key={employee.id}>
                  <td>{employee.check_employee_id}</td>
                  <td>{employee.legal_first_name}</td>
                  <td>{employee.legal_last_name}</td>
                  <td>
                    <div>
                      {!employee.fixed ? (
                        <Timer size={40} color="red" />
                      ) : hasIssues(employee) ? (
                        <Warning2 size={40} color="#fbbf24" />
                      ) : (
                        <TickCircle size={40} color="green" />
                      )}
                    </div>
                  </td>
                  <td>
                    <ul>
                      {employee.noValidBankAccount && <li>The employee has no valid bank accounts</li>}
                      {employee.noCheckOutTime && <li>The employee has no checkout time</li>}
                      {employee.checkInAfterCheckout && (
                        <li>The employee&apos;s check-in time is after their check-out time</li>
                      )}
                      {employee.jobDurationTooLong && (
                        <li>The employee&apos;s hours worked is greater than 16 hours</li>
                      )}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Base>
  );
};

export default FixPayrollModal;
